/** @jsxImportSource @emotion/react */

import useDocumentDetails from "../../../../hooks/useDocumentDetails";
import { useMemo } from "react";
import Typography from "../../../../components/Typography";
import EditableSection from "../../../../components/EditableSection";
import {
  DocumentStages,
  GetEditCropOnDocumentFormResponseDocument,
  GetEditCropOnDocumentFormResponseQueryVariables,
} from "../../../../graphql/__GENERATED__/types";
import useDocumentOrder from "../../../../hooks/useDocumentOrder";
import Table from "../../../../components/Table";
import DetailsSectionColumns from "../../../../components/Details/components/Main/Grid/DetailsSectionColumns";

const CropData: React.FC = () => {
  const {
    documentData: { crop: cropData, kmlFile, _id: documentId, stage },
    handleKmlDownload,
    refetch,
  } = useDocumentDetails();

  const cropInfo = useMemo(() => {
    const kml = [
      {
        label: "Arquivo KML",
        value: (
          <Typography
            variant="textMedium"
            component="a"
            onClick={handleKmlDownload}
            css={{ cursor: "pointer", textDecoration: "underline" }}
          >
            {kmlFile?.fileName}
          </Typography>
        ),
      },
    ];
    const culture = [
      {
        label: "Cultura",
        value: cropData?.cultureName,
      },
      {
        label: "Variedade predominante",
        value: cropData?.cultureDetail?.name || "não aplicável",
      },
    ];
    const other = [
      {
        label: "Área total",
        value: `${cropData?.area} ha`,
      },
      {
        label: "Ano do Plantio",
        value: `${cropData?.cropYear}º`,
      },
    ];
    return kmlFile ? [[...culture, ...kml], other] : [culture, other];
  }, [cropData, kmlFile]);

  const currentStage = useDocumentOrder(stage!);
  const policyStage = useDocumentOrder(DocumentStages.Policy);

  const editQuery = useMemo(
    () =>
      currentStage < policyStage
        ? GetEditCropOnDocumentFormResponseDocument
        : undefined,
    [currentStage, policyStage]
  );

  const plots = useMemo(
    () =>
      cropData?.plots?.map((plot) => ({
        ...plot,
        soilType: plot.soilType?.label,
        cultureDetail: plot.cultureDetail?.name,
      })),
    [cropData?.plots]
  );

  return (
    <EditableSection<{}, GetEditCropOnDocumentFormResponseQueryVariables>
      title="Dados da Lavoura"
      main
      footerButtons={[]}
      createPayload={{}}
      editQuery={editQuery}
      editPayload={{ documentId: documentId!, objectId: cropData?._id! }}
      objectId={cropData?._id}
      onSubmit={refetch}
    >
      <DetailsSectionColumns columns={cropInfo} />
      {plots && plots.length > 0 && (
        <Table
          data={plots}
          columns={[
            { key: "name", label: "Nome" },
            { key: "area", label: "Área" },
            { key: "plantDate", label: "Data do Plantio", special: "date" },
            { key: "soilType", label: "Tipo de Solo" },
            { key: "plantMethod", label: "Método do Plantio" },
            { key: "irrigated", label: "Irrigado", special: "boolean" },
            { key: "cropYear", label: "Ano do Plantio" },
            { key: "plantSpacing", label: "Esp. Plantas" },
            { key: "lineSpacing", label: "Esp. Linhas" },
          ]}
        />
      )}
    </EditableSection>
  );
};

export default CropData;
